import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";

const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

/** Mini-calendário compacto dos próximos plantões do consultor logado (7 dias
 * a partir de hoje). Deixa o consultor confirmar presença com 1 clique —
 * reduz buracos de cobertura porque o admin sabe quem vai aparecer.
 */
export const NextPlantoesWidget = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/consultant/me/schedule");
      setSlots(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deriva próximos 7 dias (a partir de hoje). Cada slot recebe a data absoluta
  // do próximo plantão daquele weekday.
  const now = new Date();
  const todayIdx = (now.getDay() + 6) % 7; // Mon=0..Sun=6
  const upcoming = slots
    .map((s) => {
      const daysAhead = (s.weekday - todayIdx + 7) % 7;
      const date = new Date(now);
      date.setDate(now.getDate() + daysAhead);
      date.setHours(s.start_hour, 0, 0, 0);
      return { ...s, date, daysAhead };
    })
    .filter((s) => {
      // filtra plantões já vencidos hoje
      if (s.daysAhead === 0 && s.end_hour <= now.getHours()) return false;
      return true;
    })
    .sort((a, b) => a.date - b.date)
    .slice(0, 5);

  const confirm = async (slotId) => {
    setBusyId(slotId);
    try {
      await api.post(`/consultant/schedule/${slotId}/confirm`);
      toast.success("Presença confirmada — obrigado!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusyId(null); }
  };

  if (loading) return null;
  if (upcoming.length === 0) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="next-plantoes-widget">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarCheck className="w-5 h-5 text-[#0B2B62]" />
          <h3 className="font-display font-bold text-lg text-[#0B2B62]">Próximos plantões</h3>
        </div>
        <span className="text-xs text-slate-500">{upcoming.length} plantão{upcoming.length > 1 ? "s" : ""} nos próximos 7 dias</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {upcoming.map((s) => {
          const confirmed = !!s.confirmed_at;
          const relative = s.daysAhead === 0 ? "Hoje" : s.daysAhead === 1 ? "Amanhã" : `Em ${s.daysAhead} dias`;
          return (
            <div key={s.id} data-testid={`plantao-slot-${s.id}`}
              className={`p-3 rounded-lg border ${confirmed ? "border-green-300 bg-green-50/50" : "border-slate-200 bg-white"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{relative}</div>
                  <div className="font-display font-bold text-[#0B2B62] mt-0.5">
                    {WEEKDAYS[s.weekday]} · {String(s.start_hour).padStart(2, "0")}:00–{String(s.end_hour).padStart(2, "0")}:00
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{s.date.toLocaleDateString("pt-BR", { day:"2-digit", month:"2-digit"})}</div>
                </div>
                {confirmed && <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />}
              </div>
              {confirmed ? (
                <div className="mt-2 text-[10px] text-green-700 font-semibold uppercase tracking-widest">Presença confirmada</div>
              ) : (
                <button data-testid={`plantao-confirm-${s.id}`} disabled={busyId === s.id} onClick={() => confirm(s.id)}
                  className="mt-2 w-full px-2.5 py-1.5 border border-[#0B2B62] text-[#0B2B62] rounded-md text-xs font-semibold hover:bg-[#0B2B62] hover:text-white transition-colors disabled:opacity-50">
                  {busyId === s.id ? "Confirmando…" : "Confirmar presença"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default NextPlantoesWidget;
